import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ProductDetail from '../components/product/ProductDetail';
import api from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const ProductPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const initialSize = searchParams.get('size') || '';
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();
  
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setLoading(true);
        const productData = await api.get(`/products/${id}`);
        setProduct(productData);
        setReviews(productData.reviews || []);

        const allProducts = await api.get('/products');
        const related = allProducts
          .filter(p => p.category === (productData.category?._id || productData.category) && p._id !== productData._id)
          .slice(0, 4);
        setRelatedProducts(related);

        const viewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
        const filteredViewed = viewed.filter(pId => pId !== productData._id).slice(0, 4);
        const viewedProducts = allProducts.filter(p => filteredViewed.includes(p._id));
        setRecentlyViewed(viewedProducts);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchProductData();
    }
  }, [id]);

  useEffect(() => {
    if (product?._id) {
      const viewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
      const updated = [product._id, ...viewed.filter(id => id !== product._id)].slice(0, 10);
      localStorage.setItem('recentlyViewed', JSON.stringify(updated));
    }
  }, [product]);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to submit a review');
      return;
    }
    if (!comment.trim()) {
      toast.error('Please write a review message');
      return;
    }

    setSubmittingReview(true);
    try {
      const res = await api.post(`/products/${id}/reviews`, { rating, comment });
      setReviews(prev => [...prev, res.review]);
      setComment('');
      setRating(5);
      toast.success('Review submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) return <LoadingSpinner fullScreen text="Loading product details..." />;
  if (error) return <div className="text-center py-20 text-red-600">{error}</div>;
  if (!product) return <div className="text-center py-20">Product not found</div>;

  const primaryColor = theme.primaryColor;
  const textColor = theme.textColor;

  return (
    <div className="bg-white min-h-screen" style={{ backgroundColor: theme.backgroundColor }}>
      <ProductDetail product={product} initialSize={initialSize} />
      
      <div className="max-w-[1550px] mx-auto px-4 md:px-10 py-10">
        <h2 className="text-2xl font-light text-center mb-6" style={{ color: textColor }}>
          You May Also Like
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {relatedProducts.map((item) => (
            <Link to={`/product/${item._id}`} key={item._id} className="group cursor-pointer block" onClick={() => window.scrollTo(0,0)}>
              <div className="relative overflow-hidden mb-3">
                <img src={item.images?.[0] || 'https://via.placeholder.com/400'} alt={item.name} className="w-full aspect-[3/4] object-cover transition-transform duration-300 group-hover:scale-105" />
                {item.originalPrice > item.price && <div className="absolute bottom-3 left-3"><span className="bg-[#e60000] text-white text-[10px] font-bold px-2 py-1">Save {Math.round(((item.originalPrice - item.price) / item.originalPrice) * 100)}%</span></div>}
              </div>
              <h3 className="text-xs md:text-sm font-medium text-[#1c1d1d] uppercase mb-1 truncate">{item.name}</h3>
              <div className="flex flex-wrap items-center gap-2">
                {item.originalPrice > item.price && <span className="text-xs md:text-sm text-gray-400 line-through">Rs. {item.originalPrice.toLocaleString()}</span>}
                <span className="text-xs md:text-sm font-medium text-[#1c1d1d]">Rs. {item.price.toLocaleString()}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="max-w-[1550px] mx-auto px-4 md:px-10 py-10 border-t" style={{ borderColor: theme.borderColor || '#e5e5e5' }}>
        <h2 className="text-2xl font-light text-center mb-6" style={{ color: textColor }}>
          Recently Viewed
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {recentlyViewed.map((item) => (
            <Link to={`/product/${item._id}`} key={item._id} className="group cursor-pointer block" onClick={() => window.scrollTo(0,0)}>
              <div className="relative overflow-hidden mb-3">
                <img src={item.images?.[0] || 'https://via.placeholder.com/400'} alt={item.name} className="w-full aspect-[3/4] object-cover transition-transform duration-300 group-hover:scale-105" />
              </div>
              <h3 className="text-xs md:text-sm font-medium text-[#1c1d1d] uppercase mb-1 truncate">{item.name}</h3>
              <div className="flex flex-wrap items-center gap-2">
                {item.originalPrice > item.price && <span className="text-xs md:text-sm text-gray-400 line-through">Rs. {item.originalPrice.toLocaleString()}</span>}
                <span className="text-xs md:text-sm font-medium text-[#1c1d1d]">Rs. {item.price.toLocaleString()}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="max-w-[1550px] mx-auto px-4 md:px-10 py-10 border-t" style={{ borderColor: theme.borderColor || '#e5e5e5' }}>
        <h3 className="text-2xl font-normal mb-6" style={{ color: textColor }}>Customer Reviews</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            {reviews.length === 0 ? (
              <p className="text-gray-500">No reviews yet. Be the first to review this product!</p>
            ) : (
              <div className="space-y-6 max-h-[400px] overflow-y-auto pr-4">
                {reviews.map((rev, idx) => (
                  <div key={idx} className="border-b pb-4" style={{ borderColor: theme.borderColor || '#e5e5e5' }}>
                    <div className="flex text-yellow-400 text-sm mb-1">
                      {[...Array(5)].map((_, i) => <span key={i}>{i < rev.rating ? '★' : '☆'}</span>)}
                    </div>
                    <p className="font-semibold text-sm capitalize" style={{ color: textColor }}>{rev.name || rev.user?.name || 'Verified Buyer'}</p>
                    <p className="text-gray-600 text-sm mt-1">{rev.comment}</p>
                    {rev.createdAt && <p className="text-gray-400 text-xs mt-1">{new Date(rev.createdAt).toLocaleDateString()}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="p-6 rounded" style={{ backgroundColor: theme.borderColor || '#f3f3f3' }}>
            <h4 className="font-medium mb-4" style={{ color: textColor }}>Write a Review</h4>
            <form onSubmit={handleReviewSubmit} className="space-y-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: textColor }}>Rating</label>
                <select 
                  value={rating} 
                  onChange={(e) => setRating(Number(e.target.value))} 
                  className="w-full border p-2.5 rounded bg-white outline-none focus:ring-1"
                  style={{ borderColor: theme.borderColor || '#ccc', color: textColor }}
                >
                  <option value={5}>5 - Excellent</option>
                  <option value={4}>4 - Good</option>
                  <option value={3}>3 - Average</option>
                  <option value={2}>2 - Poor</option>
                  <option value={1}>1 - Terrible</option>
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: textColor }}>Review Message</label>
                <textarea 
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows="4" 
                  className="w-full border p-2.5 rounded bg-white outline-none resize-none focus:ring-1"
                  style={{ borderColor: theme.borderColor || '#ccc', color: textColor }}
                  required 
                  placeholder="Share your experience about this product..."
                />
              </div>
              <button 
                type="submit" 
                disabled={submittingReview}
                className="text-white px-6 py-3 uppercase tracking-widest font-medium transition-colors w-full rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
              >
                {submittingReview ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;